import React, { useEffect, useState } from 'react';
import PreviewSMSPage from './PreviewSMSPage';
import { generateSmsMessage } from '../../services/AiService';
import { sendQuickSms } from '../../services/SmsService';
import { getUserSenderIds, SenderIdDto } from '../../services/SenderIdService';
import { fetchUserTemplates, TemplateRecord } from '../../services/TemplateService';

// Add CSS for animation
const notificationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .animate-fade-in {
    animation: fadeIn 0.3s ease-in-out forwards;
  }
`;

// removed local generateAIMessage; using backend service

function getCurrentUserId(): string | null {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.id || null;
  } catch (_) {
    return null;
  }
}

export const SMSPage: React.FC = () => {
  const [message, setMessage] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [symbolsLeft, setSymbolsLeft] = useState(160);
  const [smsCount, setSmsCount] = useState(1);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [addToTemplate, setAddToTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [messageOverview, setMessageOverview] = useState<string>('');
  const [generatedMessage, setGeneratedMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [senderId, setSenderId] = useState<string>('');
  const [availableSenderIds, setAvailableSenderIds] = useState<SenderIdDto[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [messageGoal, setMessageGoal] = useState<string>('Promotional Message');
  const [messageTone, setMessageTone] = useState<string>('Friendly');
  const [templates, setTemplates] = useState<TemplateRecord[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string>('');
  const [modal, setModal] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
    subMessage?: string;
  } | null>(null);

  const validatePhoneNumber = (number: string): boolean => {
    // Remove any spaces, dashes, or other formatting
    const cleaned = number.replace(/[\s\-\(\)]/g, '');
    
    // Check if it starts with 263 and is exactly 12 digits
    if (!cleaned.startsWith('263')) {
      return false;
    }
    
    // Check if it's exactly 12 digits
    if (cleaned.length !== 12 || !/^\d{12}$/.test(cleaned)) {
      return false;
    }
    
    return true;
  };

  const handleAddRecipient = () => {
    setPhoneNumberError('');
    
    if (!phoneNumber.trim()) {
      setPhoneNumberError('Please enter a phone number');
      return;
    }

    // Clean the phone number (remove spaces, dashes, etc.)
    const cleaned = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    // Validate the phone number
    if (!validatePhoneNumber(cleaned)) {
      setPhoneNumberError('Phone number must start with 263 and be exactly 12 digits (e.g., 263712345678)');
      return;
    }

    // Check for duplicates
    if (recipients.includes(cleaned)) {
      setPhoneNumberError('This number is already in the recipients list');
      return;
    }

    // Add the cleaned number
    setRecipients([...recipients, cleaned]);
    setPhoneNumber('');
    setPhoneNumberError('');
  };

  const handleRemoveRecipient = (numberToRemove: string) => {
    setRecipients(recipients.filter(number => number !== numberToRemove));
  };

  const handlePreview = () => {
    setIsPreviewMode(true);
  };

  const handleEdit = () => {
    setIsPreviewMode(false);
  };

  useEffect(() => {
    const init = async () => {
      const uid = getCurrentUserId();
      if (!uid) return;
      try {
        const list = await getUserSenderIds(uid);
        setAvailableSenderIds(list);
        if (list.length && !senderId) setSenderId(list[0].senderId);
      } catch (_) {
        // ignore silently; dropdown will remain empty
      }
    };
    init();
  }, []);

  // Fetch user templates on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        const data = await fetchUserTemplates();
        setTemplates(data);
      } catch (error) {
        console.error('Failed to load templates:', error);
        // Silently fail - dropdown will just be empty
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, []);

  // Handle template selection
  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    
    if (templateId) {
      const selectedTemplate = templates.find(t => t.id === templateId);
      if (selectedTemplate) {
        setMessage(selectedTemplate.content || '');
      }
    } else {
      // If "Choose template" is selected, optionally clear the message
      // Or leave it as is to allow user to keep editing
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setModal({ type: 'error', title: 'Message required', message: 'Please enter a message.' });
      return;
    }
    if (recipients.length === 0) {
      setModal({ type: 'error', title: 'No recipients', message: 'Please add at least one recipient.' });
      return;
    }
    setIsSending(true);
    try {
      const resp = await sendQuickSms({ senderId, body: message, recipients });
      setModal({
        type: 'success',
        title: 'Messages queued',
        message: `${resp.queued} recipient(s) queued for sending.`,
        subMessage: resp.invalid.length > 0 ? `${resp.invalid.length} number(s) were invalid and skipped.` : undefined
      });
      // Optionally reset form
      // setMessage(''); setRecipients([]);
    } catch (e: any) {
      setModal({ type: 'error', title: 'Failed to send SMS', message: e?.message || 'Failed to send SMS' });
    } finally {
      setIsSending(false);
    }
  };

  const handleGenerateMessage = async () => {
    if (!messageOverview.trim()) {
      setModal({ type: 'error', title: 'Overview required', message: 'Please provide a message overview.' });
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const response = await generateSmsMessage({ 
        overview: messageOverview, 
        goal: messageGoal,
        tone: messageTone
      });
      setGeneratedMessage(response);
    } catch (error) {
      console.error('Error generating message:', error);
      setModal({ type: 'error', title: 'Generation failed', message: 'Failed to generate message. Please try again.' });
    } finally {
      setIsGenerating(false);
    }
  };

  // Function to handle using the generated message
  const handleUseGeneratedMessage = () => {
    setMessage(generatedMessage);
    
    // Show notification
    setShowNotification(true);
    
    // Hide notification after 2 seconds
    setTimeout(() => {
      setShowNotification(false);
      // Navigate to preview page
      setIsPreviewMode(true);
    }, 1500);
  };

  const sendResultModal = modal && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
        <div className="flex flex-col items-center px-6 py-6 text-center">
          <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${modal.type === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
            {modal.type === 'success' ? (
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{modal.title}</h3>
          <p className="mt-2 text-sm text-gray-600">{modal.message}</p>
          {modal.subMessage && (
            <p className="mt-1 text-sm text-red-600">{modal.subMessage}</p>
          )}
        </div>
        <div className="flex justify-center border-t border-gray-100 px-6 py-4">
          <button
            onClick={() => setModal(null)}
            className="rounded-md bg-[#FF5722] px-6 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-[#F4511E]"
          >
            {modal.type === 'success' ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );

  if (isPreviewMode) {
    return (
      <>
        <style>{notificationStyles}</style>
        <PreviewSMSPage
          message={message}
          recipients={recipients}
          onEdit={handleEdit}
          onSend={handleSend}
        />
        {sendResultModal}
      </>
    );
  }

  return (
    <>
      <style>{notificationStyles}</style>
    <div className="flex text-sm">
      <div className="flex-1">
        {/* Top Banner */}
        <div className="bg-white border-b">
          <div className="px-6 py-4">
            <h1 className="text-xl font-bold">Quick SMS</h1>
          </div>
        </div>

        {/* SMS Form Section */}
        <div className="p-6 max-w-4xl mx-auto bg-white rounded-lg mt-6">
          <div className="space-y-6">
            {/* Sender and Recipients Row */}
            <div className="flex items-start space-x-4">
              {/* Sender Dropdown */}
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Sender I.D.
                </label>
                <select 
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  value={senderId}
                  onChange={(e) => setSenderId(e.target.value)}
                >
                  {availableSenderIds.length === 0 && (
                    <option value="">Select sender ID</option>
                  )}
                  {availableSenderIds.map(s => (
                    <option key={s.id} value={s.senderId}>{s.senderId}</option>
                  ))}
                </select>
              </div>

              {/* Phone Number Input */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add recipients
                </label>
                <div className="flex space-x-2">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      setPhoneNumberError(''); // Clear error when user types
                    }}
                    placeholder="Phone number (e.g., 263712345678)"
                    className={`flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent ${
                      phoneNumberError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddRecipient();
                      }
                    }}
                  />
                  <button 
                    onClick={handleAddRecipient}
                    className="bg-[#FF5722] text-white p-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
                {phoneNumberError && (
                  <p className="mt-1 text-sm text-red-600">{phoneNumberError}</p>
                )}
              </div>
            </div>

            {/* Recipients List Box */}
            <div className="mt-4 border border-gray-200 rounded-md bg-white p-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-medium text-gray-700">Recipients</h3>
                <span className="text-sm text-gray-500">{recipients.length} number(s)</span>
              </div>
              {recipients.length > 0 ? (
                <div className="space-y-2">
                  {recipients.slice(0, 5).map((number, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between bg-gray-50 p-2 rounded-md"
                    >
                      <span className="text-sm text-gray-600">{number}</span>
                      <button
                        onClick={() => handleRemoveRecipient(number)}
                        className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {recipients.length > 5 && (
                    <div className="text-center py-2 text-sm text-gray-500 italic">
                      ... and {recipients.length - 5} more recipient(s)
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-gray-500">
                  No recipients added yet
                </div>
              )}
            </div>

            {/* Message and Template Section */}
            <div className="grid grid-cols-3 gap-6">
              {/* Message Area */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add SMS message
                </label>

                {/* Templates Dropdown - Moved here */}
                <div className="mb-3">
                  <select 
                    value={selectedTemplateId}
                    onChange={handleTemplateChange}
                    disabled={isLoadingTemplates}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Choose template</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  {isLoadingTemplates && (
                    <p className="text-xs text-gray-500 mt-1">Loading templates...</p>
                  )}
                </div>

                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={8}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>

              {/* Templates and Counters */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total characters in SMS message
                  </label>
                  <div className="flex items-center justify-between p-2 border border-gray-300 rounded-md bg-gray-50">
                    <span className="text-sm text-gray-600">Characters used:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">{message.length}</span>
                      <span className="text-sm text-gray-500">/ 160</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({Math.ceil(message.length / 160)} SMS)
                      </span>
                    </div>
                  </div>
                </div>            
              </div>
            </div>
            {/* Middle Action Buttons */}
            {false && (            
            <div className="flex justify-between pt-4 pb-6">
                <button 
                  onClick={handlePreview}
                  className="bg-[#FF5722] text-white px-6 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200"
                >
                Preview SMS
              </button>
              <button className="bg-gray-100 text-gray-600 px-6 py-2 rounded-md hover:bg-gray-200 transition-colors duration-200">
                Next SMS
              </button>
            </div>
            )}
            {/* AI SMS Assistant Section */}
            {false && (
            <div className="border border-gray-200 rounded-md bg-white p-6 space-y-4">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold">AI SMS Assistant</h3>
                <svg className="w-5 h-5 text-[#FF5722]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
                  <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
                </svg>
              </div>
              
              <p className="text-sm text-gray-600">
                Generate personalized SMS messages in seconds.
              </p>

              {/* Message Goal Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message Goal
                </label>
                <select 
                  value={messageGoal}
                  onChange={(e) => setMessageGoal(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                >
                  <option>Promotional Message</option>
                  <option>Informational Update</option>
                  <option>Event Reminder</option>
                  <option>Customer Service</option>
                </select>
              </div>

              {/* Tone Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tone
                </label>
                <select 
                  value={messageTone}
                  onChange={(e) => setMessageTone(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                >
                  <option>Friendly</option>
                  <option>Professional</option>
                  <option>Casual</option>
                  <option>Formal</option>
                  <option>Urgent</option>
                  <option>Assertive</option>
                </select>
              </div>

              {/* Total Symbols Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Characters in SMS message
                </label>
                <div className="flex items-center justify-between p-2 border border-gray-300 rounded-md bg-gray-50">
                  <span className="text-sm text-gray-600">Characters used:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">{message.length}</span>
                    <span className="text-sm text-gray-500">/ 160</span>
                    <span className="text-xs text-gray-500 ml-2">
                      ({Math.ceil(message.length / 160)} SMS)
                    </span>
                  </div>
                </div>
              </div>

                {/* Message Overview Section */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message Overview
                </label>
                  <textarea
                    value={messageOverview}
                    onChange={(e) => setMessageOverview(e.target.value)}
                    placeholder="Enter your message overview here..."
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    rows={4}
                  />
                </div>

                {/* Generate Message Button */}
                <button
                  onClick={handleGenerateMessage}
                  disabled={isGenerating}
                  className="w-full bg-[#FF5722] text-white py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </span>
                  ) : (
                    'Generate Message'
                  )}
                </button>

                {/* AI Generated Message */}
                {generatedMessage && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-md">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">AI Generated Message</h3>
                    <p className="text-sm text-gray-600 whitespace-pre-line">{generatedMessage}</p>

                    {/* Use Generated Message Button */}
                    <button 
                      className="mt-2 w-full bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200"
                      onClick={handleUseGeneratedMessage}
                    >
                      Use Generated Message & Preview
              </button>

                    {/* Success Notification */}
                    {showNotification && (
                      <div className="mt-2 p-3 bg-green-100 text-green-800 rounded-md text-center animate-fade-in">
                        Message applied successfully! Redirecting to preview...
                      </div>
                    )}
              </div>
                )}

              {/* Add to Template Checkbox and SMS Count */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="addToTemplate"
                    checked={addToTemplate}
                    onChange={(e) => setAddToTemplate(e.target.checked)}
                    className="h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
                  />
                  <label htmlFor="addToTemplate" className="text-sm text-gray-700">
                    Add to Template
                  </label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-700">SMS Count:</span>
                  <span className="text-sm font-medium text-gray-900">1</span>
                </div>
              </div>

              {/* Template Name Input - Only shows when checkbox is checked */}
              {addToTemplate && (
                <div className="animate-fade-in">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name of Template
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter template name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  />
                </div>
              )}
            </div>
            )}

            {/* Bottom Action Buttons */}
            <div className="flex justify-between pt-4">
              <button 
                onClick={handlePreview}
                className="bg-[#FF5722] text-white px-6 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200"
              >
                Preview SMS
              </button>
              <button 
                onClick={handleSend}
                disabled={isSending}
                className="bg-gray-100 text-gray-600 px-6 py-2 rounded-md hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
              >
                {isSending ? 'Sending…' : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    {sendResultModal}
    </>
  );
}; 

export {};
